/**
 * harness/useUrlParams.js
 * T-Cron-2026-10-23 03:30 (round-1-of-day) — 改进清单 #12「PWA 快捷方式契约」
 *
 * 为什么需要:
 *   `public/manifest.json` 声明了 PWA 快捷方式:
 *       { "name": "今天的内容", "url": "/?filter=today" }
 *   但 **Vite 树里一处 URL 参数处理都没有** ——
 *   `grep -rn "URLSearchParams|searchParams|location.search" src/` → 0 命中。
 *
 *   后果:用户长按 App 图标 → 点「今天的内容」→ 浏览器打开 `/?filter=today`
 *   → 看板**完整加载,但筛选状态完全没变**,看起来像"快捷方式坏了"。
 *   这是 PWA 快捷方式最坏的失败模式:看起来是 bug,不是被忽略。
 *
 *   根目录老仓库(App.jsx:631-700)有一份成熟实现,处理了
 *   `?action=search` / `?action=export` / `?action=newest` / `?view=favorites`。
 *   Vite 迁移时这段逻辑**整体丢失**,manifest 却保留了 shortcut 声明 ⇒
 *   声明与实现脱节。老仓库的教训(2026-10-20 T95 轮)已经明确写过:
 *   **manifest 声明的每个 URL 都必须有对应 handler,否则就是死链。**
 *
 * 本文件是 Vite 版的 URL 参数契约层:
 *   ?filter=today      → 只看最新一天的选题(等价于老仓库的 ?action=newest)
 *   ?view=favorites    → 只看已选素材
 *   ?action=search     → 聚焦搜索框
 *   ?action=export     → 导出当前可见内容(CSV)
 *
 * 设计约束:
 *   1. **只读一次**:mount-only effect + 立刻 history.replaceState 抹掉 query string。
 *      否则 back/forward 或 React StrictMode 双调用会重复触发同一个动作。
 *   2. **回调放 ref 里**:opts 在 render 期赋值(不是 useEffect),
 *      避免 effect 执行时拿到上一帧的回调。
 *   3. **未知参数一律忽略**:不 strip、不报错 —— 第三方链接带乱七八糟的
 *      utm 参数是常态,不能因此把用户的 URL 改掉。
 *
 * 注意:本文件是 .js(不是 .jsx)⇒ **必须显式 import React**
 * (vite.config.js 的 esbuild.jsxInject 只对 .jsx 生效)。
 */
import React from 'react';

/**
 * @param {Object} opts
 * @param {() => void} [opts.onFilterToday]   ?filter=today
 * @param {() => void} [opts.onFilterFavorites] ?view=favorites
 * @param {() => void} [opts.onSearch]         ?action=search
 * @param {() => void} [opts.onExport]         ?action=export
 */
function useUrlParams(opts) {
  const optsRef = React.useRef(opts);
  // render 期赋值 —— effect 执行时拿到的必然是最新回调。
  optsRef.current = opts;

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const view = params.get('view');
    const filter = params.get('filter');

    // 没有任何已知参数 ⇒ 完全不碰 URL(第三方 utm 参数不该被抹掉)
    if (!action && !view && !filter) return;

    const o = optsRef.current || {};

    // 已知参数才 strip。未知 action(如 ?action=share)保留在 URL 里,
    // 免得吞掉别人(如 SW share_target)的参数。
    const known = !!(
      (filter && o.onFilterToday) ||
      (view === 'favorites' && o.onFilterFavorites) ||
      (action === 'search' && o.onSearch) ||
      (action === 'export' && o.onExport)
    );
    if (known) {
      try {
        window.history.replaceState({}, '', window.location.pathname + window.location.hash);
      } catch (_) {}
    }

    if (filter === 'today' && o.onFilterToday) {
      o.onFilterToday();
      return;
    }
    if (view === 'favorites' && o.onFilterFavorites) {
      o.onFilterFavorites();
      return;
    }
    if (action === 'search' && o.onSearch) {
      o.onSearch();
      return;
    }
    if (action === 'export' && o.onExport) {
      o.onExport();
      return;
    }
  }, []);
}

if (typeof window !== 'undefined') {
  window.useUrlParams = useUrlParams;
}

export default useUrlParams;
